import debounce from 'lodash/debounce';
import React, { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqualObjects } from 'shallow-equal';
import MpTree from 'src/common/betaComponents/MpTree';
import { NodeState, NodesState } from 'src/common/betaComponents/MpTree/types';
import { Input, notification, Spinner, Form } from 'src/common';
import styles from './index.module.scss';
import { IPreferencesTreeNode } from './types';
import { ApolloError } from '@apollo/client';
import SingleLineSkeleton from 'src/common/betaComponents/SingleLineSkeleton';
import {
  toCheckedKeys,
  filterCheckedKeys,
  filterExactCheckedKeys,
  toNodesState
} from 'src/common/betaComponents/MpTree/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/pro-light-svg-icons';
import classNames from 'classnames';
import { useLoadMp } from 'src/models/matchingProfiles/hooks';
import { MpCodeTypes } from 'src/models/matchingProfiles/types';
import { trackMPEditSearchCPVCode, trackMPEditSearchRegion } from 'src/segment/events';
import { Description } from 'src/shared/Blocks';

interface IPreferencesTreeProps {
  availableNodes: readonly IPreferencesTreeNode[];

  checkedKeys: readonly string[];
  checkedKeysExact?: readonly string[];

  updateCheckedKeys: (checkedKeys: string[], exactCheckedKeys: string[]) => void;
  updateCheckedKeysError?: ApolloError;
  checkedKeysUpdating: boolean;

  updatingErrorTitle?: string;
  cardTitle?: string;
  cardDescription?: string;
  cardDescription2?: string;
  cardIcon?: ReactNode;
  disabled: boolean;
  type: MpCodeTypes;

  showKey?: boolean;
  checkStrictly?: boolean;

  enableImport?: boolean;
  className?: string;
}

interface IFormState {
  keyword: string;
}

export const PreferencesTree: FC<IPreferencesTreeProps> = props => {
  const {
    availableNodes,
    checkedKeys,
    checkedKeysExact,
    updateCheckedKeys,
    updateCheckedKeysError,
    checkedKeysUpdating,
    updatingErrorTitle,
    cardTitle,
    cardDescription,
    cardDescription2,
    cardIcon,
    disabled,
    type,
    showKey,
    checkStrictly,
    enableImport,
    className
  } = props;

  const exactCheckedKeys = useMemo(
    () => (checkStrictly && checkedKeysExact ? checkedKeysExact : []),
    [checkStrictly, checkedKeysExact]
  );

  const [searchPhrase, setSearchPhrase] = useState('');
  const setSearchDebounce = debounce((value: string) => {
    if (!value || value.length === 0 || value?.length > 1) {
      setSearchPhrase(value);

      /** @todo should be moved out to MpTree-references (aka where it used) */
      if (mp) {
        if (type === 'cpvCodes') {
          trackMPEditSearchCPVCode({ id: mp.id, name: mp.name });
        } else if (type === 'nutsCodes') {
          trackMPEditSearchRegion({ id: mp.id, name: mp.name });
        }
      }
    }
  }, 500);

  const [importing, setImporting] = useState(false);
  const showSpinner = useMemo(() => {
    return enableImport && importing && checkedKeysUpdating;
  }, [checkedKeysUpdating, enableImport, importing]);

  const { t } = useTranslation();
  const { data: mp } = useLoadMp();

  const [form] = Form.useForm();
  const initialValues = useMemo((): IFormState => {
    return {
      keyword: ''
    };
  }, []);

  useEffect(() => {
    if (updateCheckedKeysError) {
      notification.error({
        description: t('Common.unknownErrorDesc'),
        message: updatingErrorTitle
      });
    }
  }, [t, updatingErrorTitle, updateCheckedKeysError]);
  const [nodesState, setNodesState] = useState<NodesState>(() => toNodesState(checkedKeys, exactCheckedKeys));
  useEffect(() => {
    /**
     *  @note: checkedKeys often is changed by an update from this component.
     *    In that case there is high chance that nextNodesState has the same value but not the same reference
     *    as the current one. To avoid double render we can do compare by value
     */
    setNodesState(prevState => {
      const nextState = toNodesState(checkedKeys, exactCheckedKeys);
      return shallowEqualObjects(prevState, nextState) ? prevState : nextState;
    });
  }, [checkedKeys, exactCheckedKeys]);

  const onCheck = useCallback(
    (nextNodesState: NodesState) => {
      // optimistic update
      setNodesState(nextNodesState);
      setImporting(false);

      if (checkStrictly) {
        updateCheckedKeys(filterCheckedKeys(nextNodesState), filterExactCheckedKeys(nextNodesState));
      } else {
        updateCheckedKeys(toCheckedKeys(nextNodesState), []);
      }
    },
    [checkStrictly, setImporting, updateCheckedKeys]
  );

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const keyword: string = form.getFieldValue('keyword');
      if (enableImport && keyword) {
        const keywordsObject = Object.fromEntries(keyword.split(',').map(item => [item, NodeState.ExactChecked]) || []);
        const newNodesState = {
          ...nodesState,
          ...keywordsObject
        };
        updateCheckedKeys(filterCheckedKeys(newNodesState), filterExactCheckedKeys(newNodesState));

        form.resetFields();
        setSearchDebounce('');
        setImporting(true);
      }
    }
  };

  const onKeywordChange = useCallback(
    (changedValues: Partial<IFormState>) => {
      setSearchDebounce(changedValues.keyword || '');
    },
    [setSearchDebounce]
  );

  return (
    <div className={className}>
      <div className={styles.headerRow}>
        {cardIcon && <span className={styles.headerIcon}>{cardIcon}</span>}
        {cardTitle && <span className={styles.headerTitle}>{cardTitle}</span>}
        {checkedKeysUpdating && <SingleLineSkeleton loading active />}
        {(cardDescription || cardDescription2) && (
          <Description>
            <div className={styles.headerDesc}>{cardDescription}</div>
            {cardDescription2 && (
              <div className={classNames(styles.headerDesc, styles.subDesc)}>{cardDescription2}</div>
            )}
          </Description>
        )}
        {!disabled && (
          <div className={styles.searchBar}>
            <Form form={form} component={false} initialValues={initialValues} onValuesChange={onKeywordChange}>
              <Form.Item name="keyword">
                <Input
                  type="text"
                  onClick={event => event.stopPropagation()}
                  onKeyDown={onKeyDown}
                  suffix={<FontAwesomeIcon icon={faSearch} />}
                  placeholder={t('Common.search')}
                />
              </Form.Item>
            </Form>
          </div>
        )}
      </div>
      {!showSpinner ? (
        <MpTree
          searchPhrase={searchPhrase}
          tree={availableNodes}
          nodesState={nodesState}
          onCheck={onCheck}
          disabled={disabled}
          showKey={!!showKey}
          checkStrictly={!!checkStrictly}
          type={type}
          mp={mp ? { id: mp.id, name: mp.name } : undefined}
        />
      ) : (
        <Spinner className={styles.spinner} text={t('Common.spinnerText')} isLarge />
      )}
    </div>
  );
};

export default PreferencesTree;
